import React, { useState, useEffect } from 'react';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import AuthScreen from './components/AuthScreen';
import SignUpScreen from './components/SignUpScreen';
import EquipmentContent from './components/EquipmentContent';
import HousePlanner from './features/house_planner/HouserPlanner';
import VisitReservation from './components/VisitReservation';
import ConfirmedSummary from './components/ConfirmedSummary';
import './index.css'; // 必要に応じてスタイルシートをインポート
// react-router-dom のコンポーネントをインポート
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import AdminUserDetail from './components/AdminUserDetail';
import UserManagement from './components/UserManagement';
import LikeImagesWithChat from './components/LikeImagesWithChat';
import FloorPlanner from './features/house_planner/components/FloorPlanner';
import UserInviteAccept from './components/UserInviteAccept';
import AnythingChat from './components/AnythingChat';
import UserInfo from './components/UserInfo';
import MasterEquipment from './components/MasterEquipment';

// 認証が必要なルートを保護するコンポーネント
const ProtectedRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  if (isAdmin) {
    return <Navigate to="/admin/users" />;
  }

  return children;
};

// 管理者用の保護されたルート
const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  // 管理者でなければ認証画面へリダイレクト
  if (!currentUser || !isAdmin) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

function AppContent() {
  const { loading, isFunctionEnabled } = useAuth(); // loading 状態はここでチェック

  if (loading) {
    // アプリケーション全体のローディング表示
    return <div>アプリケーションを読み込んでいます...</div>;
  }

  return (
    // Routes で囲み、各パスに対応するコンポーネントを Route で定義
    <Routes>
      {/* 認証画面用のルート */}
      <Route path="/auth" element={<AuthScreen />} />
      
      {/* 新規登録画面用のルート */}
      <Route path="/signup" element={<SignUpScreen />} />

      {/* 招待画面用のルート */}
      <Route path="/user_invite/accept" element={<UserInviteAccept />} />

      {/* メインの設備選択画面用のルート (認証が必要) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {/* EquipmentContent には onFixedChange が必要なら渡す */}
            <EquipmentContent onFixedChange={() => {}} />
          </ProtectedRoute>
        }
      />

      {/* 間取りプランナー用のルート (認証が必要) */}
      <Route
        path="/planner" // '/planner' パスにアクセスした場合
        element={
          <ProtectedRoute>
            <HousePlanner /> {/* HousePlanner コンポーネントを表示 */}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user/:targetUserId/planner" // '/planner' パスにアクセスした場合
        element={
          <AdminRoute>
            <HousePlanner /> {/* HousePlanner コンポーネントを表示 */}
          </AdminRoute>
        }
      />

      <Route
        path="/admin/master-equipment" 
        element={
          <AdminRoute>
            <MasterEquipment /> {/* MasterEquipment コンポーネントを表示 */}
          </AdminRoute>
        }
      />

      <Route
        path="/admin/master-floor" 
        element={
          <AdminRoute>
            <HousePlanner /> {/* HouserPlanner コンポーネントを表示 */}
          </AdminRoute>
        }
      />


      {/* 来店予約画面用のルート (認証が必要) */}
      <Route
        path="/visit-reservation"
        element={
          <ProtectedRoute>
            <VisitReservation />
          </ProtectedRoute>
        }
      />

      {/* 確定アイテム一覧用のルート (認証が必要) */}
      <Route
        path="/confirmed-summary"
        element={
          <ProtectedRoute>
            <ConfirmedSummary />
          </ProtectedRoute>
        }
      />

      {/* 管理者用の顧客の確定アイテム一覧用のルート */}
      <Route
        path="/admin/user/:targetUserId/confirmed-summary"
        element={
          <AdminRoute>
            <ConfirmedSummary />
          </AdminRoute>
        }
      />

      <Route
        path="/like-images"
        element={
          <ProtectedRoute>
            <LikeImagesWithChat />
          </ProtectedRoute>
        }
      />

      {/* 管理者用の顧客の確定アイテム一覧用のルート */}
      <Route
        path="/admin/user/:targetUserId/like-images"
        element={
          <AdminRoute>
            <LikeImagesWithChat />
          </AdminRoute>
        }
      />

      {/* チャット機能が有効な場合のみルートを表示 */}
      {isFunctionEnabled('chat') && (
        <>
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <AnythingChat />
              </ProtectedRoute>
            }
          />

          {/* 管理者用の顧客の確定アイテム一覧用のルート */}
          <Route
            path="/admin/user/:targetUserId/chat"
            element={
              <AdminRoute>
                <AnythingChat />
              </AdminRoute>
            }
          />
        </>
      )}  

      <Route
        path="/admin/user/:targetUserId/planner"
        element={
          <AdminRoute>
            <HousePlanner />
          </AdminRoute>
        }
      />

      {/* 管理者用ルート */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/user/:userId"
        element={
          <AdminRoute>
            <AdminUserDetail />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/user-info"
        element={ 
          <AdminRoute>
            <UserInfo isAdmin={true} />
          </AdminRoute>
        }
      />
      <Route
        path="/user-info"
        element={
          <ProtectedRoute>
            <UserInfo />
          </ProtectedRoute>
        }
      />

      {/* 上記以外のパスはルート (/) へリダイレクト (任意) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}
